/**
 * Integrytas Group - CSM. Active languages as {code, name}, primary (current UI
 * locale) first. Backs LocaleSelect and TranslationsEditor. Every name comes
 * from localeName, so one function names a language everywhere (see
 * language_options_core for why the curated _tl names are no longer consulted).
 */
import { useEffect, useState } from 'react';
import { apiDb } from './api_client';
import { useActiveLocale } from './use_active_locale';
import { localeName } from '../i18n/locale_display';
import { buildLanguageOptions, type LanguageOption, type LangRow } from './language_options_core';

export interface LanguageOptions {
  options: LanguageOption[];
  primary: string;
  loading: boolean;
}

export function useLanguageOptions(): LanguageOptions {
  const activeLocale = useActiveLocale();
  const [state, setState] = useState<{ options: LanguageOption[]; loading: boolean }>({ options: [], loading: true });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb
        .from('csm_language_master')
        .select('internal_code, is_active')
        .eq('is_active', 'Y')
        .order('internal_code');
      const rows: LangRow[] = ((data ?? []) as { internal_code: string }[]).map(r => ({ code: r.internal_code }));
      const options = buildLanguageOptions(rows, activeLocale, activeLocale, localeName);
      if (!cancelled) setState({ options, loading: false });
    })();
    return () => { cancelled = true; };
  }, [activeLocale]);

  return { options: state.options, primary: activeLocale, loading: state.loading };
}
